import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import appLogo from '../../assets/app-logo.svg';
import flytrexLogo from '../../assets/flytrex-logo.svg';
import signInBackground from '../../assets/sign-in-background.webp';
import { PrimaryButton } from '../../components/buttons/PrimaryButton';
import DropDownMenu from '../../components/general/DropDownMenu';
import { Text } from '../../components/general/Text';
import { GCS_CONFIGS } from '../../config/gcs/gcsConfigs';
import { PENDING_SIGN_IN_GCS_ID_KEY } from '../../constants/auth';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { en } from '../../locales/en';
import { useSelectedGcsConfigsStore } from '../../store/useSelectedGcsConfigsStore';
import { parseSerializedAppError } from '../../types/errors/errors';
import { AsyncAction } from '../../types/general/asyncAction';
import { GcsServerCheckboxTile } from './GcsServerCheckboxTile';

const pageContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  gap: '2rem',
  padding: '2rem',
  backgroundImage: `url(${signInBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  position: 'relative',
  boxSizing: 'border-box',
  overflow: 'hidden'
};

const headerLogoStyle: React.CSSProperties = {
  height: '24px',
  filter: 'brightness(0) invert(1)'
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  padding: '32px',
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: 'var(--card-border-radius)',
  width: '100%',
  maxWidth: '400px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
};

const headerContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '24px',
  left: '24px',
  right: '24px',
  display: 'flex',
  justifyContent: 'space-between'
};

const serversListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  width: '100%'
};

const welcomeTextStyle: React.CSSProperties = {
  color: 'var(--brand-primary)',
  fontWeight: '800',
  fontSize: '2.5rem',
  textAlign: 'center'
};

const errorMessageStyle: React.CSSProperties = {
  color: 'var(--color-error)',
  marginTop: '0.75rem',
  display: 'block',
  textAlign: 'center'
};

function SignInPage() {
  const { oktaAuth } = useOktaAuth();
  const location = useLocation();
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const { selectedConfigs, setSelectedGcsConfigs } = useSelectedGcsConfigsStore();
  const [signInAction, setSignInAction] = useState<AsyncAction<void, unknown>>(AsyncAction.initial());
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedConfigs.map((c) => c.id)));
  const callbackErrorFromQuery = parseSerializedAppError(new URLSearchParams(location.search).get('state'));

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (!queryParams.has('state')) {
      return;
    }

    queryParams.delete('state');
    const nextQuery = queryParams.toString();
    const nextUrl = `${location.pathname}${nextQuery ? `?${nextQuery}` : ''}${location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [location.hash, location.pathname, location.search]);

  const effectiveError = signInAction.isFailure ? signInAction.error : callbackErrorFromQuery;

  const onGcsServerToggled = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const onSignInWithOktaClicked = async () => {
    const ids = Array.from(selectedIds);
    try {
      setSignInAction(AsyncAction.loading());
      setSelectedGcsConfigs(new Set(ids));
      localStorage.setItem(PENDING_SIGN_IN_GCS_ID_KEY, ids[0]);
      await oktaAuth.signInWithRedirect();
      setSignInAction(AsyncAction.success());
    } catch (error) {
      setSignInAction(AsyncAction.failure(error));
    }
  };

  const onAppLogoClicked = () => {
    setIsVersionDropdownOpen((prev) => !prev);
  };

  return (
    <div style={pageContainerStyle}>
      <div style={headerContainerStyle}>
        <img src={flytrexLogo} alt='Flytrex' style={headerLogoStyle} />
        <div style={{ position: 'relative' }}>
          <button
            type='button'
            onClick={onAppLogoClicked}
            aria-label='About'
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}
          >
            <img src={appLogo} alt='Remote Operations Center' style={headerLogoStyle} />
          </button>

          <DropDownMenu
            isOpen={isVersionDropdownOpen}
            onClose={() => setIsVersionDropdownOpen(false)}
            top='100%'
            right='0'
          >
            <div
              style={{
                minWidth: '144px',
                textAlign: 'center'
              }}
            >
              <Text variant='body2'>
                {__APP_VERSION__} | {__COMMIT_HASH__}
              </Text>
            </div>
          </DropDownMenu>
        </div>
      </div>

      <div style={cardStyle}>
        <Text variant='h1' style={welcomeTextStyle}>
          {en.signIn.title}
        </Text>
        <Text variant='h1'>{en.signIn.subtitle}</Text>
        <div style={serversListStyle}>
          {GCS_CONFIGS.map((config) => (
            <GcsServerCheckboxTile
              key={config.id}
              id={config.id}
              name={config.name}
              isSelected={selectedIds.has(config.id)}
              onToggle={onGcsServerToggled}
            />
          ))}
        </div>
        <div style={{ width: '100%' }}>
          <PrimaryButton
            text={en.signIn.signInWithOktaButton}
            callback={onSignInWithOktaClicked}
            isEnabled={selectedIds.size > 0 && !signInAction.isLoading}
            isLoading={signInAction.isLoading}
          />
          {effectiveError ? (
            <Text variant='body3' style={errorMessageStyle}>
              {getLocalizedErrorMessage(effectiveError)}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SignInPage;
